/**
 * M2 P4 / Wave-0 §2: projectile (weapon-bolt) renderer. A small fixed pool of
 * matte tracer cores + a faint velocity-aligned streak, placed by floating-origin
 * delta like npcs.ts. Visual size/readability only — combat collision stays in
 * sim-core.
 *
 * COLD-MATTE TONE (wave0-engineering-reference §2): bodies are NormalBlending +
 * toneMapped:true so they sit INSIDE the ACES grade and never cross the 1.0 bloom
 * threshold (AAA-21 — mourning surfaces never glow; only Sol blooms). Friend/foe
 * read by VALUE/SHAPE, not neon hue. Per-weapon: the fitted WeaponProfile's
 * `size` drives core geometry and `projectileSpeedKmS` drives streak length, so
 * different fitted weapons look distinct.
 */
import * as THREE from 'three';
import { type Vec3, type Projectile, type WeaponProfile } from '@blackmarble/sim-core';

const POOL = 32;
export const BOLT_LEN_KM = 0.6;
export const BOLT_CORE_RADIUS_KM = 0.02;

// Cold/matte palette — re-used by combatFx.ts for muzzle/impact tints. Friend
// reads ash-white, foe a dimmer red-grey (distinguished by value, not hue).
export const PLAYER_BOLT_COLOR = 0xcfd2c8; // ash-white core
export const HOSTILE_BOLT_COLOR = 0x8a4a44; // hostile dimmer red-grey
const PLAYER_STREAK_COLOR = 0x6a6f66; // cold steel
const HOSTILE_STREAK_COLOR = 0x5a3b38; // charcoal red-grey
const STREAK_RADIUS_KM = 0.05; // ~0.10 km wide streak
const STREAK_OPACITY = 0.4;

/** Per-bolt visual derived from a fitted weapon's mount class + projectile speed. */
export interface BoltVisual {
  coreColor: number;
  streakColor: number;
  coreRadiusKm: number;
  coreLenKm: number;
  streakLenKm: number;
}

/** Core geometry (radius km, length km) by hardpoint mount size. */
const CORE_BY_SIZE: Record<string, readonly [number, number]> = {
  small: [0.018, 0.45],
  medium: [0.026, 0.6],
  large: [0.034, 0.78],
};

/** Faster rounds (railgun) leave a longer streak; slow rounds a stubby one. */
const streakLenFor = (speedKmS: number): number => Math.max(0.6, Math.min(1.0, speedKmS / 60));

/** Pure: build a bolt visual from a fitted weapon profile (or a partial spec). */
export const boltVisualFor = (
  w: Pick<WeaponProfile, 'size' | 'projectileSpeedKmS'>,
  isPlayer: boolean,
): BoltVisual => {
  const [coreRadiusKm, coreLenKm] = CORE_BY_SIZE[w.size] ?? CORE_BY_SIZE.small!;
  return {
    coreColor: isPlayer ? PLAYER_BOLT_COLOR : HOSTILE_BOLT_COLOR,
    streakColor: isPlayer ? PLAYER_STREAK_COLOR : HOSTILE_STREAK_COLOR,
    coreRadiusKm,
    coreLenKm,
    streakLenKm: streakLenFor(w.projectileSpeedKmS),
  };
};

const DEFAULT_PLAYER_VISUAL = boltVisualFor({ size: 'small', projectileSpeedKmS: 40 }, true);
const HOSTILE_VISUAL = boltVisualFor({ size: 'small', projectileSpeedKmS: 40 }, false);

const cylinderGeo = (radiusKm: number, lenKm: number): THREE.CylinderGeometry => {
  const geo = new THREE.CylinderGeometry(radiusKm, radiusKm, lenKm, 8);
  geo.rotateX(Math.PI / 2); // axis → local +Z so lookAt aligns it to velocity
  return geo;
};

type BoltSlot = {
  core: THREE.Mesh<THREE.CylinderGeometry, THREE.MeshBasicMaterial>;
  streak: THREE.Mesh<THREE.CylinderGeometry, THREE.MeshBasicMaterial>;
};

export class ProjectileRenderer {
  private readonly slots: BoltSlot[] = [];
  private readonly geoCache = new Map<string, THREE.CylinderGeometry>();
  private readonly coreMats = new Map<number, THREE.MeshBasicMaterial>();
  private readonly streakMats = new Map<number, THREE.MeshBasicMaterial>();
  private playerVisual: BoltVisual = DEFAULT_PLAYER_VISUAL;

  constructor(parent: THREE.Group) {
    for (let i = 0; i < POOL; i++) this.slots.push(this.createSlot(parent));
  }

  /** Wire the player's fired bolts to the active fitted weapon's visual. */
  setPlayerWeapon(weapon?: Pick<WeaponProfile, 'size' | 'projectileSpeedKmS'>): void {
    this.playerVisual = weapon ? boltVisualFor(weapon, true) : DEFAULT_PLAYER_VISUAL;
  }

  private createSlot(parent: THREE.Group): BoltSlot {
    const core = new THREE.Mesh(this.geo(BOLT_CORE_RADIUS_KM, BOLT_LEN_KM), this.coreMat(0xffffff));
    const streak = new THREE.Mesh(this.geo(STREAK_RADIUS_KM, BOLT_LEN_KM), this.streakMat(0xffffff));
    core.visible = false;
    streak.visible = false;
    parent.add(streak); // streak under core so the matte core reads on top
    parent.add(core);
    return { core, streak };
  }

  private geo(radiusKm: number, lenKm: number): THREE.CylinderGeometry {
    const key = `${radiusKm.toFixed(3)}:${lenKm.toFixed(3)}`;
    let g = this.geoCache.get(key);
    if (!g) {
      g = cylinderGeo(radiusKm, lenKm);
      this.geoCache.set(key, g);
    }
    return g;
  }

  private coreMat(color: number): THREE.MeshBasicMaterial {
    let m = this.coreMats.get(color);
    if (!m) {
      m = new THREE.MeshBasicMaterial({ color, toneMapped: true });
      this.coreMats.set(color, m);
    }
    return m;
  }

  private streakMat(color: number): THREE.MeshBasicMaterial {
    let m = this.streakMats.get(color);
    if (!m) {
      m = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: STREAK_OPACITY,
        depthWrite: false,
        toneMapped: true,
      });
      this.streakMats.set(color, m);
    }
    return m;
  }

  private applyVisual(slot: BoltSlot, v: BoltVisual): void {
    slot.core.geometry = this.geo(v.coreRadiusKm, v.coreLenKm);
    slot.core.material = this.coreMat(v.coreColor);
    slot.streak.geometry = this.geo(STREAK_RADIUS_KM, v.streakLenKm);
    slot.streak.material = this.streakMat(v.streakColor);
  }

  /** Place up to POOL active bolts (floating-origin); hide the rest. */
  update(projectiles: readonly Projectile[], shipWorldPos: Vec3): void {
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i]!;
      const p = projectiles[i];
      if (!p) {
        slot.core.visible = false;
        slot.streak.visible = false;
        continue;
      }
      this.applyVisual(slot, p.ownerId === 'player' ? this.playerVisual : HOSTILE_VISUAL);
      slot.core.visible = true;
      slot.streak.visible = true;
      const x = p.pos.x - shipWorldPos.x;
      const y = p.pos.y - shipWorldPos.y;
      const z = p.pos.z - shipWorldPos.z;
      slot.core.position.set(x, y, z);
      slot.streak.position.set(x, y, z);
      slot.core.lookAt(x + p.vel.x, y + p.vel.y, z + p.vel.z);
      slot.streak.lookAt(x + p.vel.x, y + p.vel.y, z + p.vel.z);
    }
  }
}
